import { useRef, useState } from "react";
import { UploadSimple, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { uploadSketch, removeSketch } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SketchUpload — drag-and-drop or click to upload a rough sketch.
 * Once uploaded, Claude vision describes it and the agent uses that as a
 * visual taste anchor for future scouts. Also pinned to the moodboard.
 */
export const SketchUpload = ({ recce, onUpdated }) => {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const hasSketch = !!recce.sketch_asset_id;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please drop an image (PNG, JPG, etc).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Sketch is over 8 MB — try a smaller export.");
      return;
    }
    setBusy(true);
    try {
      const updated = await uploadSketch(recce.id, file);
      toast("Sketch received. The agent is studying it…", {
        description: "It'll bias future scouts toward your visual cue.",
      });
      onUpdated?.(updated);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Upload failed. Try again in a minute.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!hasSketch) return;
    setBusy(true);
    try {
      await removeSketch(recce.id);
      onUpdated?.({ ...recce, sketch_asset_id: null, sketch_description: "" });
      toast("Sketch removed.");
    } catch (err) {
      toast.error("Couldn't remove the sketch.");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <section
      data-testid="sketch-upload"
      className="border border-line bg-surface p-5 rounded-2xl"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[1.2px] text-amber">
            visual anchor
          </p>
          <h3 className="font-serif text-xl mt-1">Drop a rough sketch</h3>
        </div>
        {hasSketch && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-ink-dim hover:text-amber disabled:opacity-40"
            data-testid="sketch-remove-btn"
            aria-label="remove sketch"
          >
            <Trash size={12} /> remove
          </button>
        )}
      </div>

      {hasSketch ? (
        <div className="space-y-3" data-testid="sketch-preview">
          <img
            src={`${API}/assets/${recce.sketch_asset_id}`}
            alt="Uploaded sketch"
            className="w-full block border border-line rounded-lg"
            style={{ maxHeight: 280, objectFit: "cover" }}
          />
          {recce.sketch_description && (
            <p
              className="font-serif italic text-sm leading-snug text-ink-dim border-l-2 border-amber pl-3"
              data-testid="sketch-description"
            >
              {recce.sketch_description}
            </p>
          )}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-ink-dim hover:text-amber disabled:opacity-40"
            data-testid="sketch-replace-btn"
          >
            <UploadSimple size={12} /> {busy ? "uploading…" : "replace sketch"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => !busy && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={busy}
          data-testid="sketch-dropzone"
          className={`w-full border-2 border-dashed rounded-xl py-9 px-5 flex flex-col items-center gap-2 transition-colors ${
            dragOver
              ? "border-amber bg-surface-hi"
              : "border-line hover:border-amber-dim hover:bg-surface-hi/50"
          } ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
        >
          <PencilSimple size={22} weight="thin" className="text-ink-dim" />
          <span className="font-serif text-base">
            {busy ? "uploading & analysing…" : "drag a sketch in, or click to browse"}
          </span>
          <span className="text-[11px] uppercase tracking-[0.12em] text-ink-ghost">
            png · jpg · webp · up to 8 mb
          </span>
        </button>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="sketch-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
    </section>
  );
};
